import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { playSfxByTag, triggerSound } from "../../lib/soundBus";
import SoundPicker from "./SoundPicker";
import SheetSection from "./SheetSection";
import SheetBioSection from "./SheetBioSection";
import SheetInventorySection from "./SheetInventorySection";
import SheetSpellSlotsSection from "./SheetSpellSlotsSection";
import SheetActionsSection from "./SheetActionsSection";
import ItemCard from "./ItemCard";
import DamageMitigationEditor from "./DamageMitigationEditor";
import { CustomFeaturesSection } from "./CustomFeaturesSection";
import { X, Volume2, FileText, Skull, ScanLine } from "lucide-react";
import RollPrivacyControl from "./RollPrivacyControl";
import OcrImportModal from "./OcrImportModal";
import LevelUpBoostModal from "./LevelUpBoostModal";
import PendingLevelSlotsPanel from "./PendingLevelSlotsPanel";
import AbilityBreakdownPopover from "./AbilityBreakdownPopover";
import TraitBlurb, { cap } from "./TraitBlurb";

const LEVEL_MILESTONES = [5, 10, 15, 20];

const SKILLS = ["acrobatics", "arcana", "athletics", "crafting", "deception", "diplomacy", "intimidation", "medicine", "nature", "occultism", "performance", "religion", "society", "stealth", "survival", "thievery"];
const RANKS = ["untrained", "trained", "expert", "master", "legendary"];
const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
const WEAPON_GROUPS = ["unarmed", "simple", "martial", "advanced"];
const SAVES = ["fortitude", "reflex", "will"];

export default function CharacterSheetFull({ gameId, character, onCharacterUpdate, isGm, members, myMemberId }) {
  const [stats, setStats] = useState(null);
  const [feats, setFeats] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [spellsCatalog, setSpellsCatalog] = useState([]);
  const [ancestries, setAncestries] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [classes, setClasses] = useState([]);
  const [soundSetupOpen, setSoundSetupOpen] = useState(new Set());
  const [openAbilityBreakdown, setOpenAbilityBreakdown] = useState(null);
  const [rollPrivacy, setRollPrivacy] = useState({ is_private: false, whisper_target_member_id: null });

  const ancestryObj = character.ancestry_slug ? ancestries.find((a) => a.slug === character.ancestry_slug) : null;
  const backgroundObj = character.background_slug ? backgrounds.find((b) => b.slug === character.background_slug) : null;
  const classObj = character.class_slug ? classes.find((c) => c.slug === character.class_slug) : null;
  const ancestryName = ancestryObj?.name || character.custom_ancestry_name || "";
  const backgroundName = backgroundObj?.name || character.custom_background_name || "";
  const className = classObj?.name || character.custom_class_name || "";

  const loadStats = useCallback(async () => {
    const { data } = await api.get(`/characters/${character.id}/stats`);
    setStats(data);
  }, [character.id]);

  useEffect(() => {
    loadStats();
  }, [loadStats, character]);

  useEffect(() => {
    api.get("/pf2e/weapons").then((r) => setWeapons(r.data));
    api.get("/pf2e/spells").then((r) => setSpellsCatalog(r.data));
    api.get("/pf2e/ancestries").then((r) => setAncestries(r.data));
    api.get("/pf2e/backgrounds").then((r) => setBackgrounds(r.data));
    api.get("/pf2e/classes").then((r) => setClasses(r.data));
  }, []);

  useEffect(() => {
    if (character.class_slug) {
      Promise.all([
        api.get("/pf2e/feats", { params: { klass: character.class_slug } }),
        api.get("/pf2e/feats", { params: { ancestry: character.ancestry_slug } }),
        api.get("/pf2e/feats", { params: { type: "general" } }),
        api.get("/pf2e/feats", { params: { type: "skill" } }),
      ]).then(([c, a, g, s]) => {
        const merged = [...c.data, ...a.data, ...g.data, ...s.data];
        const unique = Array.from(new Map(merged.map((f) => [f.slug, f])).values());
        setFeats(unique);
      });
    } else {
      setFeats([]);
    }
  }, [character.ancestry_slug, character.class_slug]);

  const roll = async (label, modifier, sfxTag) => {
    try {
      await api.post(`/characters/${character.id}/roll`, {
        game_id: gameId, label, modifier,
        is_private: rollPrivacy.is_private, whisper_target_member_id: rollPrivacy.whisper_target_member_id,
      });
      if (sfxTag) playSfxByTag(gameId, sfxTag);
      if (rollPrivacy.whisper_target_member_id) {
        const targetName = (members || []).find((m) => m.id === rollPrivacy.whisper_target_member_id)?.display_name || "player";
        toast.success(`Rolled secretly — only you and ${targetName} can see this`);
      } else if (rollPrivacy.is_private) {
        toast.success(isGm ? "Rolled privately — only you can see this" : "Rolled privately — only you and the GM can see this");
      }
    } catch {
      toast.error("Roll failed");
    }
  };

  const rollDamage = async (label, dice, bonus) => {
    try {
      await api.post(`/characters/${character.id}/damage-roll`, {
        game_id: gameId, label, dice, bonus,
        is_private: rollPrivacy.is_private, whisper_target_member_id: rollPrivacy.whisper_target_member_id,
      });
    } catch {
      toast.error("Damage roll failed");
    }
  };

  const patchChar = async (updates) => {
    const { data } = await api.patch(`/characters/${character.id}`, updates);
    onCharacterUpdate(data);
  };

  const [levelUpQueue, setLevelUpQueue] = useState([]);
  const [levelUpOpen, setLevelUpOpen] = useState(false);
  const [levelCommitInFlight, setLevelCommitInFlight] = useState(false);

  const commitLevel = async (newLevel) => {
    if (!newLevel || newLevel === character.level || levelCommitInFlight) return;
    setLevelCommitInFlight(true);
    try {
      const oldLevel = character.level;
      const applied = character.ability_boost_milestones_applied || [];
      const crossed = LEVEL_MILESTONES.filter((m) => newLevel >= m && oldLevel < m && !applied.includes(m));
      await patchChar({ level: newLevel });
      if (crossed.length > 0) {
        setLevelUpQueue(crossed);
        setLevelUpOpen(true);
      }
    } finally {
      setLevelCommitInFlight(false);
    }
  };

  const setSkillRank = (skill, rank) => {
    const skill_proficiencies = { ...character.skill_proficiencies, [skill]: rank };
    patchChar({ skill_proficiencies });
  };

  const toggleFeat = (slug) => {
    const current = character.selected_feat_slugs || [];
    const feat = feats.find((f) => f.slug === slug);
    if (!current.includes(slug) && feat) {
      const warnings = getFeatPrereqWarnings(feat);
      if (warnings.length > 0) toast.warning(`${feat.name}: ${warnings.join(", ")} — added anyway, it's your call`);
    }
    const updated = current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug];
    patchChar({ selected_feat_slugs: updated });
  };

  const getFeatPrereqWarnings = (feat) => {
    const warnings = [];
    if (character.level < feat.level) warnings.push(`needs level ${feat.level}`);
    if (feat.type === "ancestry" && feat.ancestry && character.ancestry_slug && character.ancestry_slug !== feat.ancestry) {
      warnings.push(`needs ${feat.ancestry} ancestry`);
    }
    if (feat.type === "class" && feat.class && character.class_slug && character.class_slug !== feat.class) {
      warnings.push(`needs ${feat.class} class`);
    }
    return warnings;
  };

  const addWeapon = (slug) => {
    const inventory = character.inventory || [];
    if (inventory.some((i) => i.slug === slug)) return;
    patchChar({ inventory: [...inventory, { slug, equipped: true }] });
    setSoundSetupOpen((prev) => new Set(prev).add(slug));
  };

  const removeWeapon = (slug) => {
    patchChar({ inventory: (character.inventory || []).filter((i) => i.slug !== slug) });
  };

  const setWeaponSfx = (slug, field, assetId) => {
    const inventory = (character.inventory || []).map((i) => (i.slug === slug ? { ...i, [field]: assetId } : i));
    patchChar({ inventory });
  };

  const toggleSoundSetup = (slug) => {
    setSoundSetupOpen((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  };

  const addSpell = (slug) => {
    const known = character.known_spell_slugs || [];
    if (known.includes(slug)) return;
    patchChar({ known_spell_slugs: [...known, slug] });
    setSoundSetupOpen((prev) => new Set(prev).add(slug));
  };

  const removeSpell = (slug) => {
    patchChar({ known_spell_slugs: (character.known_spell_slugs || []).filter((s) => s !== slug) });
  };

  const setSpellSfx = (slug, assetId) => {
    patchChar({ spell_sfx: { ...(character.spell_sfx || {}), [slug]: assetId } });
  };

  const markHitOrMiss = (strike, outcome) => {
    const overrideId = outcome === "hit" ? strike.hit_sfx_id : strike.miss_sfx_id;
    triggerSound(gameId, { tag: outcome === "hit" ? "attack_hit" : "attack_miss", overrideAssetId: overrideId });
    toast.success(`${strike.name}: ${outcome === "hit" ? "Hit!" : "Miss!"}`);
  };

  const castSpell = (sp) => {
    roll(`Cast ${sp.name}`, stats.spellcasting.attack, null);
    triggerSound(gameId, { tag: "spell_cast", overrideAssetId: sp.cast_sfx_id });
  };

  const switchToPaperMode = async () => {
    await patchChar({ mode: "paper" });
    toast.success("Switched to Paper Character mode");
  };

  const [ocrOpen, setOcrOpen] = useState(false);
  const importFromPhoto = async (draft) => {
    const existingWeaponSlugs = new Set((character.inventory || []).map((i) => i.slug));
    const newWeapons = (draft.inventory || []).filter((i) => !existingWeaponSlugs.has(i.slug));
    const existingSpellSlugs = new Set(character.known_spell_slugs || []);
    const newSpells = (draft.known_spell_slugs || []).filter((s) => !existingSpellSlugs.has(s));
    await patchChar({
      name: draft.name,
      ancestry_slug: draft.ancestry_slug || character.ancestry_slug,
      background_slug: draft.background_slug || character.background_slug,
      class_slug: draft.class_slug || character.class_slug,
      level: draft.level,
      abilities: draft.abilities,
      skill_proficiencies: draft.skill_proficiencies,
      hp_current: draft.hp_current ?? character.hp_current,
      hp_max: draft.hp_max ?? character.hp_max,
      currency: draft.currency,
      languages: draft.languages,
      heritage_text: draft.heritage_text,
      size: draft.size,
      inventory: [...(character.inventory || []), ...newWeapons],
      known_spell_slugs: [...(character.known_spell_slugs || []), ...newSpells],
    });
    toast.success("Sheet updated from photo");
  };

  const setWeaponProficiency = (group, rank) => {
    patchChar({ weapon_proficiencies: { ...(character.weapon_proficiencies || {}), [group]: rank } });
  };

  const setLanguages = (text) => {
    patchChar({ languages: text.split(",").map((l) => l.trim()).filter(Boolean) });
  };

  const [combatSoundsOpen, setCombatSoundsOpen] = useState(false);

  const setHpCurrent = (value) => {
    const prevHp = character.hp_current ?? stats?.max_hp ?? 0;
    patchChar({ hp_current: value });
    if (prevHp > 0 && value <= 0) {
      triggerSound(gameId, { tag: "ko", overrideAssetId: character.ko_sfx_id });
      toast.error(`${character.name} has been knocked out!`);
    }
  };

  const markDeath = async () => {
    triggerSound(gameId, { tag: "death", overrideAssetId: character.death_sfx_id });
    toast.error(`${character.name} has died.`);
    try {
      await api.post(`/characters/${character.id}/mark-death`, { game_id: gameId });
    } catch {
      toast.error("Could not mark death on the map token");
    }
  };

  const reviveCharacter = async () => {
    try {
      await api.post(`/characters/${character.id}/revive`, { game_id: gameId });
      toast.success(`${character.name} is back on their feet.`);
    } catch {
      toast.error("Could not revive");
    }
  };

  const [giftingIdx, setGiftingIdx] = useState(null);
  const [giftTarget, setGiftTarget] = useState(null);
  const lootedItems = (character.inventory || []).map((it, idx) => ({ ...it, _idx: idx })).filter((it) => it.is_card);

  const toggleEquipCard = (item) => {
    if (item.type === "armor") {
      if (character.equipped_armor && character.equipped_armor.name !== item.name) {
        if (!window.confirm(`Replace your current armor (${character.equipped_armor.name}) with ${item.name}?`)) return;
      }
      patchChar({ equipped_armor: { name: item.name, ac_bonus: item.ac_bonus || 0 } });
      return;
    }
    const inventory = (character.inventory || []).map((it, idx) => (idx === item._idx ? { ...it, equipped: !it.equipped } : it));
    patchChar({ inventory });
  };

  const sendGift = async (item) => {
    if (!giftTarget) return toast.error("Pick who to give it to");
    try {
      await api.post(`/characters/${character.id}/gift-item`, { game_id: gameId, item_index: item._idx, target_member_id: giftTarget });
      const updated = await api.get(`/characters/${character.id}`);
      onCharacterUpdate(updated.data);
      toast.success(`Gave ${item.name} away`);
      setGiftingIdx(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not gift item");
    }
  };

  if (!stats) return <div className="p-8 text-center text-[#f1e0c5]/40">Loading sheet…</div>;

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto scrollbar-thin space-y-6 font-mono-sheet" data-testid="character-sheet-full">
      <div className="sticky -top-4 md:-top-6 z-10 -mx-4 md:-mx-6 -mt-4 md:-mt-6 px-4 md:px-6 pt-4 md:pt-6 pb-3 bg-[#322216]/95 backdrop-blur-md border-b border-[#4a3424]/50 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl md:text-4xl leading-none">{character.name}</h2>
          <p className="text-xs uppercase tracking-widest text-[#D4AF37] mt-1 flex items-center gap-1.5 flex-wrap">
            <span className="flex items-center gap-1">
              Level
              <Input
                data-testid="sheet-level-input"
                type="number"
                min={1}
                max={20}
                value={character.level}
                disabled={levelCommitInFlight || levelUpOpen}
                onChange={(e) => commitLevel(parseInt(e.target.value, 10) || 1)}
                title="Editing your level to 5/10/15/20 will prompt your 4 free ability boosts"
                className="w-12 h-6 text-xs px-1.5 py-0 bg-black/30 border-white/10 text-[#D4AF37] disabled:opacity-50"
              />
            </span>
            {ancestryName} {className} {backgroundName ? `(${backgroundName})` : ""} {character.size ? `· ${character.size}` : ""}
          </p>
          <button data-testid="switch-to-paper-mode-button" onClick={switchToPaperMode} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f1e0c5]/40 hover:text-[#D4AF37] border border-[#4a3424]/50 rounded-full px-3 py-1 mt-2">
            <FileText className="w-3 h-3" /> Switch to Paper Mode
          </button>
          <button data-testid="open-ocr-import-button" onClick={() => setOcrOpen(true)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f1e0c5]/40 hover:text-[#D4AF37] border border-[#4a3424]/50 rounded-full px-3 py-1 mt-2 ml-2">
            <ScanLine className="w-3 h-3" /> Re-Import from Photo
          </button>
          <OcrImportModal open={ocrOpen} onOpenChange={setOcrOpen} gameId={gameId} isHomebrew={false} onConfirm={importFromPhoto} />
          {levelUpQueue.length > 0 && (
            <LevelUpBoostModal
              open={levelUpOpen}
              onOpenChange={setLevelUpOpen}
              milestone={levelUpQueue[0]}
              abilities={character.abilities}
              onComplete={async (newAbilities, newHistoryEntries) => {
                const doneMilestone = levelUpQueue[0];
                const nextQueue = levelUpQueue.slice(1);
                await patchChar({
                  abilities: newAbilities,
                  ability_boost_milestones_applied: [...(character.ability_boost_milestones_applied || []), doneMilestone],
                  boost_history: [...(character.boost_history || []), ...(newHistoryEntries || [])],
                });
                setLevelUpQueue(nextQueue);
                if (nextQueue.length === 0) setLevelUpOpen(false);
                toast.success(`Level ${doneMilestone} boosts applied!`);
              }}
            />
          )}
          <div className="mt-2">
            <RollPrivacyControl isGm={isGm} members={members} myMemberId={myMemberId} value={rollPrivacy} onChange={setRollPrivacy} />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="text-center bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] px-4 py-2">
            <div className="text-[10px] uppercase text-[#f1e0c5]/40">AC</div>
            <div className="text-2xl text-[#D4AF37]" data-testid="sheet-ac-value">{stats.ac}</div>
          </div>
          <div className="text-center bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] px-4 py-2">
            <div className="text-[10px] uppercase text-[#f1e0c5]/40">HP</div>
            <div className="flex items-center gap-1">
              <Input
                data-testid="sheet-hp-current-input"
                type="number"
                value={character.hp_current ?? stats.max_hp}
                onChange={(e) => setHpCurrent(parseInt(e.target.value, 10) || 0)}
                className="w-14 h-7 text-center bg-black/30 border-[#4a3424]/50 px-1"
              />
              <span className="text-[#f1e0c5]/40">/</span>
              <Input
                data-testid="sheet-hp-max-input"
                type="number"
                value={character.hp_max ?? stats.max_hp}
                onChange={(e) => patchChar({ hp_max: parseInt(e.target.value, 10) || 0 })}
                title="Override max HP (e.g. to match a homebrew rule or an imported photo) — reset to auto if you change your mind"
                className="w-12 h-7 text-center bg-black/30 border-[#4a3424]/50 px-1"
              />
              {character.hp_max != null && character.hp_max !== stats.auto_max_hp && (
                <button data-testid="reset-hp-max-auto" onClick={() => patchChar({ hp_max: null })} title="Reset to the auto-calculated max HP" className="text-[8px] uppercase text-[#D4AF37]/60 hover:text-[#D4AF37]">
                  auto
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[9px] uppercase text-[#f1e0c5]/30">Temp</span>
              <Input
                data-testid="sheet-hp-temp-input"
                type="number"
                value={character.hp_temp ?? 0}
                onChange={(e) => patchChar({ hp_temp: parseInt(e.target.value, 10) || 0 })}
                className="w-12 h-6 text-center bg-black/30 border-[#4a3424]/50 px-1 text-xs"
              />
            </div>
            <button data-testid="toggle-combat-sounds" onClick={() => setCombatSoundsOpen((v) => !v)} className="text-[9px] uppercase text-[#f1e0c5]/30 hover:text-[#D4AF37] mt-1 flex items-center gap-1">
              <Volume2 className="w-2.5 h-2.5" /> Sounds
            </button>
          </div>
          <div className="text-center bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] px-4 py-2">
            <div className="text-[10px] uppercase text-[#f1e0c5]/40">Speed</div>
            <div className="text-2xl text-[#D4AF37]">{stats.speed}ft</div>
          </div>
        </div>
      </div>

      <PendingLevelSlotsPanel character={character} feats={feats} patchChar={patchChar} />

      {combatSoundsOpen && (
        <div className="bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-3 space-y-2" data-testid="combat-sounds-panel">
          <SoundPicker gameId={gameId} value={character.ko_sfx_id} onChange={(id) => patchChar({ ko_sfx_id: id })} label="K.O. Sound (plays at 0 HP)" testId="ko-sound-picker" />
          <SoundPicker gameId={gameId} value={character.death_sfx_id} onChange={(id) => patchChar({ death_sfx_id: id })} label="Death Sound" testId="death-sound-picker" />
          <Button data-testid="mark-death-button" onClick={markDeath} size="sm" variant="secondary" className="w-full rounded-full gap-1.5 text-xs">
            <Skull className="w-3.5 h-3.5" /> Mark Character Death
          </Button>
          <Button data-testid="revive-character-button" onClick={reviveCharacter} size="sm" variant="secondary" className="w-full rounded-full gap-1.5 text-xs text-[#4d7c0f] border-[#4d7c0f]/40">
            Revive (clear Dead/Knocked Out)
          </Button>
        </div>
      )}

      {stats.senses?.length > 0 && (
        <div className="flex gap-2 flex-wrap" data-testid="sheet-senses">
          {stats.senses.map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37]">{s.replace("-", " ")}</span>
          ))}
        </div>
      )}

      <SheetSection title="Abilities" testId="sheet-section-abilities">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {ABILITIES.map((a) => (
            <div key={a} className="relative">
              <button
                data-testid={`roll-ability-${a}`}
                onClick={() => roll(`${a.toUpperCase()} Check`, stats.ability_mods[a])}
                className="w-full bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] py-3 hover:border-[#D90429] transition-colors duration-200"
              >
                <div className="text-[10px] uppercase text-[#f1e0c5]/40">{a}</div>
                <div className="text-xl text-[#D4AF37]">{stats.ability_mods[a] >= 0 ? "+" : ""}{stats.ability_mods[a]}</div>
              </button>
              <AbilityBreakdownPopover
                ability={a}
                history={character.boost_history}
                finalValue={character.abilities?.[a] ?? 10}
                open={openAbilityBreakdown === a}
                onToggle={() => setOpenAbilityBreakdown((cur) => (cur === a ? null : a))}
                testId={`ability-breakdown-panel-${a}`}
              />
            </div>
          ))}
        </div>
      </SheetSection>

      <SheetSection title="Saves, Perception &amp; Class DC" testId="sheet-section-defenses">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(stats.saves).map(([name, s]) => (
            <button key={name} data-testid={`roll-save-${name}`} onClick={() => roll(`${name} Save`, s.modifier)} className="bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] py-3 hover:border-[#D90429] transition-colors duration-200">
              <div className="text-[10px] uppercase text-[#f1e0c5]/40 capitalize">{name}</div>
              <div className="text-xl text-[#D4AF37]">{s.modifier >= 0 ? "+" : ""}{s.modifier}</div>
            </button>
          ))}
          <button data-testid="roll-perception" onClick={() => roll("Perception", stats.perception)} className="bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] py-3 hover:border-[#D90429] transition-colors duration-200">
            <div className="text-[10px] uppercase text-[#f1e0c5]/40">Perception</div>
            <div className="text-xl text-[#D4AF37]">{stats.perception >= 0 ? "+" : ""}{stats.perception}</div>
          </button>
          <div className="bg-black/30 border border-[#D4AF37]/20 rounded-xl py-3 text-center" data-testid="sheet-class-dc-value">
            <div className="text-[10px] uppercase text-[#f1e0c5]/40">Class DC</div>
            <div className="text-xl text-[#D4AF37]">{stats.class_dc}</div>
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <Select value={character.class_dc_ability || "str"} onValueChange={(v) => patchChar({ class_dc_ability: v })}>
            <SelectTrigger data-testid="class-dc-ability-select" className="h-7 bg-black/30 border-[#4a3424]/50 text-xs flex-1"><SelectValue /></SelectTrigger>
            <SelectContent>{ABILITIES.map((a) => <SelectItem key={a} value={a}>{a.toUpperCase()} Key Ability</SelectItem>)}</SelectContent>
          </Select>
          <Select value={character.class_dc_proficiency || "untrained"} onValueChange={(v) => patchChar({ class_dc_proficiency: v })}>
            <SelectTrigger data-testid="class-dc-proficiency-select" className="h-7 bg-black/30 border-[#4a3424]/50 text-xs flex-1"><SelectValue /></SelectTrigger>
            <SelectContent>{RANKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {!character.class_slug && (
          <div>
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Manual Save Proficiencies (Custom Class)</p>
            <div className="grid grid-cols-3 gap-2">
              {SAVES.map((s) => (
                <Select key={s} value={character.saves_proficiencies?.[s] || "untrained"} onValueChange={(v) => patchChar({ saves_proficiencies: { ...character.saves_proficiencies, [s]: v } })}>
                  <SelectTrigger data-testid={`manual-save-${s}`} className="h-7 bg-black/30 border-[#4a3424]/50 text-xs capitalize"><SelectValue placeholder={s} /></SelectTrigger>
                  <SelectContent>{RANKS.map((r) => <SelectItem key={r} value={r}>{s}: {r}</SelectItem>)}</SelectContent>
                </Select>
              ))}
            </div>
          </div>
        )}
        <Textarea data-testid="resistances-immunities-input" placeholder="Resistances and Immunities" value={character.resistances_immunities || ""} onChange={(e) => patchChar({ resistances_immunities: e.target.value })} className="bg-black/30 border-[#4a3424]/50 text-xs min-h-10" />
        <Textarea data-testid="conditions-notes-input" placeholder="Condition notes" value={character.conditions_notes || ""} onChange={(e) => patchChar({ conditions_notes: e.target.value })} className="bg-black/30 border-[#4a3424]/50 text-xs min-h-10" />
        <DamageMitigationEditor character={character} patchChar={patchChar} />
      </SheetSection>

      {stats.spellcasting && (
        <div className="bg-black/30 border border-[#D4AF37]/20 rounded-xl p-4 flex gap-6">
          <div>
            <div className="text-[10px] uppercase text-[#f1e0c5]/40">Spell DC</div>
            <div className="text-2xl text-[#D4AF37]">{stats.spellcasting.dc}</div>
          </div>
          <button data-testid="roll-spell-attack" onClick={() => roll("Spell Attack", stats.spellcasting.attack, "spell")} className="text-left">
            <div className="text-[10px] uppercase text-[#f1e0c5]/40">Spell Attack</div>
            <div className="text-2xl text-[#D4AF37]">{stats.spellcasting.attack >= 0 ? "+" : ""}{stats.spellcasting.attack}</div>
          </button>
          <div className="text-xs text-[#f1e0c5]/40 self-center uppercase tracking-wide">{stats.spellcasting.tradition}</div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50">Strikes</p>
          <Select value="pick" onValueChange={addWeapon}>
            <SelectTrigger data-testid="add-weapon-select" className="w-40 h-7 bg-black/30 border-[#4a3424]/50 text-xs"><SelectValue placeholder="+ Add weapon" /></SelectTrigger>
            <SelectContent>{weapons.map((w) => <SelectItem key={w.slug} value={w.slug}>{w.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          {stats.strikes.map((strike) => (
            <div key={strike.slug} className="bg-black/20 rounded-lg px-3 py-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <button data-testid={`roll-attack-${strike.slug}`} onClick={() => roll(`${strike.name} Attack`, strike.attack_bonus, "attack")} className="flex-1 min-w-[110px] text-left hover:text-[#D90429] transition-colors duration-200 truncate">
                  {strike.name} <span className="text-[#D4AF37]">{strike.attack_bonus >= 0 ? "+" : ""}{strike.attack_bonus}</span>
                </button>
                <button
                  data-testid={`roll-damage-${strike.slug}`}
                  onClick={() => rollDamage(strike.name, strike.damage_dice, strike.damage_bonus)}
                  className="text-xs text-[#f1e0c5]/50 hover:text-[#D4AF37] px-2 py-1 rounded-full border border-[#4a3424]/50 shrink-0"
                >
                  Damage {strike.damage_dice}+{strike.damage_bonus}
                </button>
                <button data-testid={`mark-hit-${strike.slug}`} onClick={() => markHitOrMiss(strike, "hit")} className="text-xs px-2 py-1 rounded-full border border-[#4d7c0f]/40 text-[#4d7c0f] hover:bg-[#4d7c0f]/10 shrink-0">Hit</button>
                <button data-testid={`mark-miss-${strike.slug}`} onClick={() => markHitOrMiss(strike, "miss")} className="text-xs px-2 py-1 rounded-full border border-[#D90429]/40 text-[#D90429] hover:bg-[#D90429]/10 shrink-0">Miss</button>
                <button data-testid={`toggle-sound-setup-${strike.slug}`} onClick={() => toggleSoundSetup(strike.slug)} className="text-[#f1e0c5]/30 hover:text-[#D4AF37] shrink-0"><Volume2 className="w-3.5 h-3.5" /></button>
                <button data-testid={`remove-weapon-${strike.slug}`} onClick={() => removeWeapon(strike.slug)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0"><X className="w-3.5 h-3.5" /></button>
              </div>
              {soundSetupOpen.has(strike.slug) && (
                <div className="mt-2 space-y-1.5 pl-1 border-l border-[#4a3424]/50 ml-1">
                  <SoundPicker gameId={gameId} value={strike.hit_sfx_id} onChange={(id) => setWeaponSfx(strike.slug, "hit_sfx_id", id)} label="Hit Sound" testId={`weapon-hit-sound-${strike.slug}`} />
                  <SoundPicker gameId={gameId} value={strike.miss_sfx_id} onChange={(id) => setWeaponSfx(strike.slug, "miss_sfx_id", id)} label="Miss Sound" testId={`weapon-miss-sound-${strike.slug}`} />
                </div>
              )}
            </div>
          ))}
          {stats.strikes.length === 0 && <p className="text-xs text-[#f1e0c5]/30">No weapons equipped.</p>}
        </div>
      </div>

      {stats.spellcasting && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50">Known Spells</p>
            <Select value="pick" onValueChange={addSpell}>
              <SelectTrigger data-testid="add-spell-select" className="w-40 h-7 bg-black/30 border-[#4a3424]/50 text-xs"><SelectValue placeholder="+ Add spell" /></SelectTrigger>
              <SelectContent>{spellsCatalog.map((sp) => <SelectItem key={sp.slug} value={sp.slug}>{sp.name} (Lv{sp.level})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            {stats.known_spells.map((sp) => (
              <div key={sp.slug} className="bg-black/20 rounded-lg px-3 py-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button data-testid={`cast-spell-${sp.slug}`} onClick={() => castSpell(sp)} className="flex-1 min-w-[110px] text-left hover:text-[#D90429] transition-colors duration-200 truncate">
                    {sp.name} <span className="text-[#f1e0c5]/30 text-xs">Lv{sp.level}</span>
                  </button>
                  {sp.damage && (
                    <button
                      data-testid={`roll-spell-damage-${sp.slug}`}
                      onClick={() => rollDamage(sp.name, sp.damage.dice, 0)}
                      className="text-xs text-[#f1e0c5]/50 hover:text-[#D4AF37] px-2 py-1 rounded-full border border-[#4a3424]/50 shrink-0"
                    >
                      Damage {sp.damage.dice}
                    </button>
                  )}
                  <button data-testid={`toggle-spell-sound-setup-${sp.slug}`} onClick={() => toggleSoundSetup(sp.slug)} className="text-[#f1e0c5]/30 hover:text-[#D4AF37] shrink-0"><Volume2 className="w-3.5 h-3.5" /></button>
                  <button data-testid={`remove-spell-${sp.slug}`} onClick={() => removeSpell(sp.slug)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0"><X className="w-3.5 h-3.5" /></button>
                </div>
                {soundSetupOpen.has(sp.slug) && (
                  <div className="mt-2 pl-1 border-l border-[#4a3424]/50 ml-1">
                    <SoundPicker gameId={gameId} value={sp.cast_sfx_id} onChange={(id) => setSpellSfx(sp.slug, id)} label="Cast Sound" testId={`spell-cast-sound-${sp.slug}`} />
                  </div>
                )}
              </div>
            ))}
            {stats.known_spells.length === 0 && <p className="text-xs text-[#f1e0c5]/30">No spells known yet.</p>}
          </div>
        </div>
      )}

      <SheetSection title="Skills" testId="sheet-section-skills">
        <div className="space-y-1">
          {SKILLS.map((skill) => {
            const s = stats.skills[skill];
            return (
              <div key={skill} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-1.5 gap-2">
                <button data-testid={`roll-skill-${skill}`} onClick={() => roll(skill, s.modifier)} className="flex-1 text-left capitalize hover:text-[#D90429] transition-colors duration-200">
                  {skill} <span className="text-[#D4AF37]">{s.modifier >= 0 ? "+" : ""}{s.modifier}</span>
                </button>
                <Select value={s.rank} onValueChange={(v) => setSkillRank(skill, v)}>
                  <SelectTrigger data-testid={`skill-rank-${skill}`} className="w-28 h-7 bg-black/30 border-[#4a3424]/50 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{RANKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      </SheetSection>

      {feats.length > 0 && (
        <SheetSection title="Feats" testId="sheet-section-feats">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto scrollbar-thin">
            {feats.map((f) => {
              const warnings = getFeatPrereqWarnings(f);
              return (
                <label key={f.slug} data-testid={`feat-${f.slug}`} className="flex items-start gap-2 bg-black/20 rounded-lg px-3 py-2 cursor-pointer">
                  <Checkbox checked={(character.selected_feat_slugs || []).includes(f.slug)} onCheckedChange={() => toggleFeat(f.slug)} />
                  <div>
                    <p className="text-sm">{f.name} <span className="text-[#f1e0c5]/30 text-xs">Lv{f.level}</span></p>
                    <p className="text-xs text-[#f1e0c5]/40 font-body">{f.description}</p>
                    {warnings.length > 0 && (
                      <p data-testid={`feat-prereq-warning-${f.slug}`} className="text-[10px] text-[#D4AF37] mt-0.5">⚠ {warnings.join(", ")}</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </SheetSection>
      )}

      {stats.class_features?.length > 0 && (
        <SheetSection title="Class Features" testId="sheet-section-class-features">
          <div className="space-y-1">
            {stats.class_features.map((f) => (
              <div key={f.name} className="text-sm bg-black/20 rounded-lg px-3 py-2">{f.name}</div>
            ))}
          </div>
        </SheetSection>
      )}

      <SheetSection title="Custom Features" testId="sheet-section-custom-features">
        <CustomFeaturesSection character={character} patchChar={patchChar} />
      </SheetSection>

      <SheetSection title="Ancestry, Background, Class &amp; Proficiencies" testId="sheet-section-proficiencies">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Ancestry</p>
            <Select value={character.ancestry_slug || "custom"} onValueChange={(v) => patchChar(v === "custom" ? { ancestry_slug: null } : { ancestry_slug: v, custom_ancestry_name: null })}>
              <SelectTrigger data-testid="sheet-ancestry-select" className="h-8 bg-black/30 border-[#4a3424]/50 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="custom">— Custom —</SelectItem>{ancestries.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
            {!character.ancestry_slug && (
              <Input data-testid="sheet-custom-ancestry-input" placeholder="Custom ancestry name" value={character.custom_ancestry_name || ""} onChange={(e) => patchChar({ custom_ancestry_name: e.target.value })} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs mt-1.5" />
            )}
            {ancestryObj && (
              <TraitBlurb
                testId="ancestry-trait-blurb"
                description={ancestryObj.description}
                facts={[
                  `Speed ${ancestryObj.speed}ft`, `Size: ${cap(ancestryObj.size)}`, `HP +${ancestryObj.hp}`,
                  ...(ancestryObj.senses || []).map(cap),
                  ...(ancestryObj.ability_flaw || []).map((a) => `${a.toUpperCase()} flaw`),
                ]}
              />
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Background</p>
            <Select value={character.background_slug || "custom"} onValueChange={(v) => patchChar(v === "custom" ? { background_slug: null } : { background_slug: v, custom_background_name: null })}>
              <SelectTrigger data-testid="sheet-background-select" className="h-8 bg-black/30 border-[#4a3424]/50 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="custom">— Custom —</SelectItem>{backgrounds.map((b) => <SelectItem key={b.slug} value={b.slug}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            {!character.background_slug && (
              <Input data-testid="sheet-custom-background-input" placeholder="Custom background name" value={character.custom_background_name || ""} onChange={(e) => patchChar({ custom_background_name: e.target.value })} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs mt-1.5" />
            )}
            {backgroundObj && (
              <TraitBlurb
                testId="background-trait-blurb"
                description={backgroundObj.description}
                facts={[`Trained: ${cap(backgroundObj.trained_skill)}`, ...(backgroundObj.trained_lore ? [backgroundObj.trained_lore] : []), `Bonus Feat: ${cap(backgroundObj.feat)}`]}
              />
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Class</p>
            <Select value={character.class_slug || "custom"} onValueChange={(v) => patchChar(v === "custom" ? { class_slug: null } : { class_slug: v, custom_class_name: null })}>
              <SelectTrigger data-testid="sheet-class-select" className="h-8 bg-black/30 border-[#4a3424]/50 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="custom">— Custom —</SelectItem>{classes.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {!character.class_slug && (
              <Input data-testid="sheet-custom-class-input" placeholder="Custom class name" value={character.custom_class_name || ""} onChange={(e) => patchChar({ custom_class_name: e.target.value })} className="bg-black/30 border-[#4a3424]/50 h-7 text-xs mt-1.5" />
            )}
            {classObj && (
              <TraitBlurb
                testId="class-trait-blurb"
                description={classObj.description}
                facts={[`Key Ability: ${(classObj.key_ability || []).map((a) => a.toUpperCase()).join("/")}`, `${classObj.hp_per_level} HP/Level`, `Perception: ${cap(classObj.perception_prof)}`]}
              />
            )}
          </div>
        </div>
        <Input data-testid="heritage-text-input" placeholder="Heritage and Traits" value={character.heritage_text || ""} onChange={(e) => patchChar({ heritage_text: e.target.value })} className="bg-black/30 border-[#4a3424]/50 h-8 text-xs" />
        <Input data-testid="languages-input" placeholder="Languages (comma separated)" value={(character.languages || []).join(", ")} onChange={(e) => setLanguages(e.target.value)} className="bg-black/30 border-[#4a3424]/50 h-8 text-xs" />
        <div>
          <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Weapon Proficiencies</p>
          <div className="grid grid-cols-2 gap-2">
            {WEAPON_GROUPS.map((g) => (
              <Select key={g} value={character.weapon_proficiencies?.[g] || "untrained"} onValueChange={(v) => setWeaponProficiency(g, v)}>
                <SelectTrigger data-testid={`weapon-proficiency-${g}`} className="h-7 bg-black/30 border-[#4a3424]/50 text-xs capitalize"><SelectValue placeholder={g} /></SelectTrigger>
                <SelectContent>{RANKS.map((r) => <SelectItem key={r} value={r}>{g}: {r}</SelectItem>)}</SelectContent>
              </Select>
            ))}
          </div>
        </div>
        <Textarea data-testid="critical-specializations-input" placeholder="Critical Specializations" value={character.critical_specializations || ""} onChange={(e) => patchChar({ critical_specializations: e.target.value })} className="bg-black/30 border-[#4a3424]/50 text-xs min-h-10" />
      </SheetSection>

      {stats.spellcasting && (
        <SheetSection title="Spell Slots, Focus &amp; Rituals" testId="sheet-section-spell-slots">
          <SheetSpellSlotsSection character={character} patchChar={patchChar} />
        </SheetSection>
      )}

      <SheetSection title="Inventory &amp; Wealth" testId="sheet-section-inventory">
        {lootedItems.length > 0 && (
          <div className="space-y-2 pb-2">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40">Looted &amp; Custom Items</p>
            {lootedItems.map((item) => (
              <ItemCard
                key={item._idx}
                card={item}
                testId={`looted-item-${item._idx}`}
                actions={
                  <>
                    <Button data-testid={`equip-looted-item-${item._idx}`} size="sm" variant="secondary" className="rounded-full text-xs h-7" onClick={() => toggleEquipCard(item)}>
                      {item.type === "armor" ? "Equip as Armor" : item.equipped ? "Unequip" : "Equip"}
                    </Button>
                    {giftingIdx === item._idx ? (
                      <div className="flex items-center gap-1.5">
                        <Select value={giftTarget || "pick"} onValueChange={setGiftTarget}>
                          <SelectTrigger data-testid={`gift-target-select-${item._idx}`} className="h-7 bg-black/30 border-[#4a3424]/50 text-xs w-32"><SelectValue placeholder="Give to…" /></SelectTrigger>
                          <SelectContent>{(members || []).filter((m) => m.id !== myMemberId).map((m) => <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button data-testid={`confirm-gift-${item._idx}`} size="sm" className="rounded-full text-xs h-7 bg-[#D90429] hover:bg-[#b8031f]" onClick={() => sendGift(item)}>Send</Button>
                      </div>
                    ) : (
                      <Button data-testid={`gift-item-${item._idx}`} size="sm" variant="secondary" className="rounded-full text-xs h-7" onClick={() => setGiftingIdx(item._idx)}>Give to Player</Button>
                    )}
                  </>
                }
              />
            ))}
          </div>
        )}
        <SheetInventorySection character={character} patchChar={patchChar} />
      </SheetSection>

      <SheetSection title="Actions, Activities &amp; Reactions" testId="sheet-section-actions">
        <SheetActionsSection character={character} patchChar={patchChar} />
      </SheetSection>

      <SheetSection title="Bio, Personality &amp; Campaign Notes" testId="sheet-section-bio">
        <SheetBioSection character={character} patchChar={patchChar} />
      </SheetSection>
    </div>
  );
}
